"use client";
import { useEffect, useRef, useState } from "react";

interface SquaresProps {
	direction?: "right" | "left" | "up" | "down" | "diagonal";
	speed?: number;
	borderColor?: string;
	squareSize?: number;
	hoverFillColor?: string;
	className?: string;
	theme?: "light" | "dark" | "auto";
}

interface ThemeColors {
	background: string;
	border: string;
	hoverFill: string;
	gradientEnd: string;
}

// Theme colors
const themeColors: Record<"light" | "dark", ThemeColors> = {
	light: {
		background: "#f8f8f8",
		border: "#ddd",
		hoverFill: "#e0e0e0",
		gradientEnd: "#f8f8f8",
	},
	dark: {
		background: "#060606",
		border: "#333",
		hoverFill: "#222",
		gradientEnd: "#060606",
	},
};

export function Squares({
	direction = "right",
	speed = 1,
	borderColor,
	squareSize = 40,
	hoverFillColor,
	className = "",
	theme = "auto",
}: SquaresProps) {
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const requestRef = useRef<number | null>(null);
	const numSquaresX = useRef<number | null>(null);
	const numSquaresY = useRef<number | null>(null);
	const gridOffset = useRef({ x: 0, y: 0 });
	const [hoveredSquare, setHoveredSquare] = useState<{
		x: number;
		y: number;
	} | null>(null);
	const [activeTheme, setActiveTheme] = useState<"light" | "dark">("dark");

	// System theme detection
	useEffect(() => {
		if (theme !== "auto") {
			setActiveTheme(theme);
			return;
		}

		// Detect system preference
		const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
		const handleChange = (e: MediaQueryListEvent | MediaQueryList) => {
			setActiveTheme(e.matches ? "dark" : "light");
		};

		// Set initial value
		handleChange(mediaQuery);

		// Listen for changes
		mediaQuery.addEventListener("change", handleChange);
		return () => mediaQuery.removeEventListener("change", handleChange);
	}, [theme]);

	// Get current theme colors
	const colors = themeColors[activeTheme];

	// Use provided colors or fallback to theme defaults
	const currentBorderColor = borderColor || colors.border;
	const currentHoverFillColor = hoverFillColor || colors.hoverFill;

	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) return;

		const ctx = canvas.getContext("2d");
		if (!ctx) return;

		// Set canvas background
		canvas.style.background = colors.background;

		const resizeCanvas = () => {
			canvas.width = canvas.offsetWidth;
			canvas.height = canvas.offsetHeight;
			numSquaresX.current = Math.ceil(canvas.width / squareSize) + 1;
			numSquaresY.current = Math.ceil(canvas.height / squareSize) + 1;
		};

		window.addEventListener("resize", resizeCanvas);
		resizeCanvas();

		const drawGrid = () => {
			ctx.clearRect(0, 0, canvas.width, canvas.height);

			const startX = Math.floor(gridOffset.current.x / squareSize) * squareSize;
			const startY = Math.floor(gridOffset.current.y / squareSize) * squareSize;

			ctx.lineWidth = 0.5;

			for (let x = startX; x < canvas.width + squareSize; x += squareSize) {
				for (let y = startY; y < canvas.height + squareSize; y += squareSize) {
					const squareX = x - (gridOffset.current.x % squareSize);
					const squareY = y - (gridOffset.current.y % squareSize);

					if (
						hoveredSquare &&
						Math.floor((x - startX) / squareSize) === hoveredSquare.x &&
						Math.floor((y - startY) / squareSize) === hoveredSquare.y
					) {
						ctx.fillStyle = currentHoverFillColor;
						ctx.fillRect(squareX, squareY, squareSize, squareSize);
					}

					ctx.strokeStyle = currentBorderColor;
					ctx.strokeRect(squareX, squareY, squareSize, squareSize);
				}
			}

			const gradient = ctx.createRadialGradient(
				canvas.width / 2,
				canvas.height / 2,
				0,
				canvas.width / 2,
				canvas.height / 2,
				Math.sqrt(canvas.width ** 2 + canvas.height ** 2) / 2,
			);
			gradient.addColorStop(0, `${colors.background.slice(0, 7)}00`); // Add transparency to background color
			gradient.addColorStop(1, colors.gradientEnd);

			ctx.fillStyle = gradient;
			ctx.fillRect(0, 0, canvas.width, canvas.height);
		};

		const updateAnimation = () => {
			const effectiveSpeed = Math.max(speed, 0.1);

			switch (direction) {
				case "right":
					gridOffset.current.x =
						(gridOffset.current.x - effectiveSpeed + squareSize) % squareSize;
					break;
				case "left":
					gridOffset.current.x =
						(gridOffset.current.x + effectiveSpeed + squareSize) % squareSize;
					break;
				case "up":
					gridOffset.current.y =
						(gridOffset.current.y + effectiveSpeed + squareSize) % squareSize;
					break;
				case "down":
					gridOffset.current.y =
						(gridOffset.current.y - effectiveSpeed + squareSize) % squareSize;
					break;
				case "diagonal":
					gridOffset.current.x =
						(gridOffset.current.x - effectiveSpeed + squareSize) % squareSize;
					gridOffset.current.y =
						(gridOffset.current.y - effectiveSpeed + squareSize) % squareSize;
					break;
			}

			drawGrid();
			requestRef.current = requestAnimationFrame(updateAnimation);
		};

		const handleMouseMove = (event: MouseEvent) => {
			const rect = canvas.getBoundingClientRect();
			const mouseX = event.clientX - rect.left;
			const mouseY = event.clientY - rect.top;

			const startX = Math.floor(gridOffset.current.x / squareSize) * squareSize;
			const startY = Math.floor(gridOffset.current.y / squareSize) * squareSize;

			const hoveredSquareX = Math.floor(
				(mouseX + gridOffset.current.x - startX) / squareSize,
			);
			const hoveredSquareY = Math.floor(
				(mouseY + gridOffset.current.y - startY) / squareSize,
			);

			setHoveredSquare({ x: hoveredSquareX, y: hoveredSquareY });
		};

		const handleMouseLeave = () => {
			setHoveredSquare(null);
		};

		// Event listeners
		window.addEventListener("resize", resizeCanvas);
		canvas.addEventListener("mousemove", handleMouseMove);
		canvas.addEventListener("mouseleave", handleMouseLeave);

		// Initial setup
		resizeCanvas();
		requestRef.current = requestAnimationFrame(updateAnimation);

		// Cleanup
		return () => {
			window.removeEventListener("resize", resizeCanvas);
			canvas.removeEventListener("mousemove", handleMouseMove);
			canvas.removeEventListener("mouseleave", handleMouseLeave);
			if (requestRef.current) {
				cancelAnimationFrame(requestRef.current);
			}
		};
	}, [
		direction,
		speed,
		currentBorderColor,
		currentHoverFillColor,
		hoveredSquare,
		squareSize,
		colors,
	]);

	return (
		<canvas
			ref={canvasRef}
			className={`w-full h-full border-none block absolute top-0 left-0 ${className}`}
		/>
	);
}
